/**
 * 主题 Store
 *
 * 管理全站主题色与明暗模式：切换即时生效（覆写根元素 CSS 变量与 data-theme）
 * 并持久化到浏览器本地存储。默认深圳大学深大红 + 亮色模式。
 */

import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import {
  DEFAULT_THEME_COLOR,
  THEME_MODES,
  THEME_PRESETS,
  applyTheme,
  buildBrandScale,
  buildBrandTextColor,
  findPresetByColor,
  normalizeHexColor,
  readStoredThemeColor,
  readStoredThemeMode,
  storeThemeColor,
  storeThemeMode,
  type ThemeMode,
  type ThemePreset
} from '@/utils/theme'

export const useThemeStore = defineStore('theme', () => {
  // ========== State ==========
  /** 当前主题色（已规范化为 #rrggbb） */
  const color = ref<string>(readStoredThemeColor())
  /** 明暗模式 */
  const mode = ref<ThemeMode>(readStoredThemeMode())
  /** 系统深色偏好（auto 模式下实时跟随） */
  const systemDark = ref(false)

  // ========== Getters ==========
  /** 预设配色 */
  const presets = computed<ThemePreset[]>(() => THEME_PRESETS)
  /** 明暗模式选项 */
  const modes = computed(() => THEME_MODES)
  /** 命中的预设（自定义颜色时为 undefined） */
  const activePreset = computed(() => findPresetByColor(color.value))
  /** 是否为默认主题色 */
  const isDefault = computed(() => color.value === DEFAULT_THEME_COLOR)
  /** 当前是否处于暗色外观 */
  const isDark = computed(() => mode.value === 'dark' || (mode.value === 'auto' && systemDark.value))
  /** 当前色阶 */
  const scale = computed(() => buildBrandScale(color.value, isDark.value))
  /** 当前品牌文字色 */
  const brandText = computed(() => buildBrandTextColor(color.value, isDark.value))

  // ========== Actions ==========

  /** 应用当前主题（颜色 + 模式） */
  function apply(): void {
    applyTheme(color.value, mode.value)
  }

  /** 设置主题色：非法值返回 false */
  function setColor(next: string): boolean {
    const normalized = normalizeHexColor(next)
    if (!normalized) return false

    color.value = normalized
    storeThemeColor(normalized)
    apply()
    return true
  }

  /** 设置明暗模式 */
  function setMode(next: ThemeMode): void {
    mode.value = next
    storeThemeMode(next)
    apply()
  }

  /** 恢复默认主题色（深圳大学深大红） */
  function resetColor(): void {
    setColor(DEFAULT_THEME_COLOR)
  }

  /** 初始化：同步系统深色偏好并监听其变化（auto 模式实时跟随） */
  function init(): void {
    const query = window.matchMedia?.('(prefers-color-scheme: dark)')

    if (query) {
      systemDark.value = query.matches
      query.addEventListener('change', (event) => {
        systemDark.value = event.matches
        if (mode.value === 'auto') {
          apply()
        }
      })
    }

    apply()
  }

  return {
    color,
    mode,
    presets,
    modes,
    activePreset,
    isDefault,
    isDark,
    scale,
    brandText,
    setColor,
    setMode,
    resetColor,
    init
  }
})
